import React, { useState } from "react";
import {
  alpha,
  Box,
  darken,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Tooltip,
  Typography,
  useTheme,
  Zoom,
} from "@mui/material";
import { BoldFlag } from "../../Icon";
import { useTranslation } from "react-i18next";
import { EGCheckbox } from "../../MainComponents/Checkbox/EGCheckbox";
import { createTags, deleteTags } from "../../../apis";
import { useParams } from "react-router";
import { EGButton } from "../../MainComponents/Button/EGButton/EGButton";
import useLocalizedLabel from "../../../hooks/useLocalizedLabel";

export const IndicatorTags = ({
  icon,
  selectedTags,
  setSelectedTags,
  transformedType,
}) => {
  // Custom Hooks
  const theme = useTheme();
  const { t } = useTranslation("report");
  const { id } = useParams();
  const getLocalizedLabel = useLocalizedLabel();
  // Initial States
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [loadingKey, setLoadingKey] = useState(null);

  // Converts an object to an array of [key, value] pairs
  const colorOptions = Object.entries(transformedType);

  // Open Menu
  const handleClickListItem = (e) => {
    setAnchorEl(e.currentTarget);
  };

  // Close Menu
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Click on menu option & send data
  const handleToggle = (key) => async () => {
    // Prevents multiple simultaneous clicks
    if (loadingKey) return;
    setLoadingKey(key);

    const currentIndex = selectedTags.indexOf(key);
    const newChecked = [...selectedTags];
    const dataToSend = { id, key };

    try {
      if (currentIndex === -1) {
        const resp = await createTags(dataToSend);
        if (resp) {
          newChecked.push(key);
          setSelectedTags(newChecked);
        }
      } else {
        const resp = await deleteTags(dataToSend);
        if (resp) {
          newChecked.splice(currentIndex, 1);
          setSelectedTags(newChecked);
        }
      }
    } catch (error) {
      console.error("Error updating tag: ", error);
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <>
      {/* Button opening menu */}
      <Tooltip
        title={t("ReportHeader.classification_tags")}
        arrow
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 150 }}
      >
        <span>
          <EGButton
            iconName={icon}
            size="thin"
            iconSize={15}
            iconColorHover="white"
            sx={{
              transition: "none",
              padding: 0,
              minWidth: 30,
              maxWidth: 30,
              "&:hover": {
                background: `linear-gradient(180deg, ${
                  theme.palette.ethicsBlue.main
                } 0%, ${darken(theme.palette.ethicsBlue.main, 0.2)} 100%)`,
                border: `1px solid transparent`,
                color: theme.palette.white.main,
              },
            }}
            onClick={handleClickListItem}
          />
        </span>
      </Tooltip>
      <Menu
        id="tag-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "tag-button",
          role: "listbox",
        }}
        PaperProps={{
          sx: {
            minWidth: 100,
            maxWidth: 300,
            px: 0.5,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {colorOptions.map(([key, value]) => {
          const labelId = `checkbox-list-label-${key}`;
          return (
            <ListItem
              key={key}
              disablePadding
              className="rounded"
              sx={{
                border: `solid 1px ${alpha(
                  theme.palette.blueGray50.main,
                  0.2
                )}`,
                "&:hover": {
                  backgroundColor: alpha(
                    theme.palette[key]?.main || theme.palette.blueGray500.main,
                    0.1
                  ),
                },
              }}
            >
              <ListItemButton
                role={undefined}
                onClick={handleToggle(key)}
                dense
              >
                <ListItemIcon sx={{ minWidth: "auto", mr: -1.5 }}>
                  <EGCheckbox
                    color="primary"
                    edge="start"
                    checked={selectedTags.includes(key)}
                    tabIndex={-1}
                    disableRipple
                    disabled={loadingKey === key}
                  />
                </ListItemIcon>

                <ListItemText
                  id={labelId}
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <BoldFlag
                          size={20}
                          color={
                            theme.palette[key]?.main ||
                            theme.palette.blueGray500.main
                          }
                        />
                      </Box>
                      <Typography
                        ml={0.75}
                        sx={{
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                          overflow: "hidden",
                        }}
                      >
                        {getLocalizedLabel(value)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </Menu>
    </>
  );
};
